import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addBrokerageManager,
  getManagedBrokerage,
  getManagedBrokerages,
  getMyBrokerage,
  removeBrokerageManager,
  saveBrokerageBranding,
  saveAgentOrder,
  saveHiddenAgents,
  saveBrokerageTeams,
  getOfficeResults,
  getLeadRouting,
  saveLeadRouting,
  startBrokeragePlanCheckout,
  confirmBrokeragePlanCheckout,
  openBrokeragePlanPortal,
  saveBrokerageSeats,
  getProRequests,
  declineProRequest,
} from "@/services/brokerageService";

export const MANAGED_BROKERAGES_KEY = ["managedBrokerages"];

const resultOf = (response, fallback) => {
  if (response?.errorCode !== 0) {
    const error = new Error(response?.message || fallback);
    error.fields = response?.data?.errors || null;
    throw error;
  }
  return response.result;
};

// The API answers bad input with an HTTP 4xx and the error envelope in the body,
// which axios throws as an AxiosError. Turn that back into the same Error shape
// resultOf builds so callers always see `message` and `fields`.
export const callOf = async (run, fallback) => {
  let response;
  try {
    response = await run();
  } catch (caught) {
    const body = caught?.response?.data;
    if (!body || typeof body !== "object") throw caught;
    response = body;
  }
  return resultOf(response, fallback);
};

export const useManagedBrokerages = (enabled = true) =>
  useQuery({
    queryKey: MANAGED_BROKERAGES_KEY,
    queryFn: () => callOf(() => getManagedBrokerages(), "Could not load your brokerage pages."),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useMyBrokerage = (enabled = true) =>
  useQuery({
    queryKey: ["myBrokerage"],
    queryFn: () => callOf(() => getMyBrokerage(), "Could not load your brokerage page."),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const managedBrokerageKey = (id) => ["managedBrokerage", id];

export const useManagedBrokerage = (id) =>
  useQuery({
    queryKey: managedBrokerageKey(id),
    queryFn: () => callOf(() => getManagedBrokerage(id), "Could not load this brokerage page."),
    enabled: Boolean(id),
  });

const useBrokerageMutation = (id, run, fallback) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => callOf(() => run(id, input), fallback),
    onSuccess: (data) => {
      queryClient.setQueryData(managedBrokerageKey(id), data);
      queryClient.invalidateQueries({ queryKey: MANAGED_BROKERAGES_KEY });
      queryClient.invalidateQueries({ queryKey: ["brokerage"] });
      queryClient.invalidateQueries({ queryKey: ["agentDirectory"] });
    },
  });
};

export const useSaveBrokerageBranding = (id) =>
  useBrokerageMutation(id, saveBrokerageBranding, "Your changes did not save.");

export const useSaveAgentOrder = (id) =>
  useBrokerageMutation(id, saveAgentOrder, "Your agent order did not save.");

export const useSaveHiddenAgents = (id) =>
  useBrokerageMutation(id, saveHiddenAgents, "That change to your page did not save.");

export const useSaveBrokerageTeams = (id) =>
  useBrokerageMutation(id, saveBrokerageTeams, "Your teams did not save.");

export const useAddBrokerageManager = (id) =>
  useBrokerageMutation(id, addBrokerageManager, "That manager could not be added.");

export const useRemoveBrokerageManager = (id) =>
  useBrokerageMutation(id, removeBrokerageManager, "That manager could not be removed.");

export const officeResultsKey = (id, days) => ["officeResults", id, days];

export const useOfficeResults = (id, days, enabled = true) =>
  useQuery({
    queryKey: officeResultsKey(id, days),
    queryFn: () => callOf(() => getOfficeResults(id, days), "Office analytics did not load."),
    enabled: Boolean(id) && enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  });

export const leadRoutingKey = (id) => ["leadRouting", id];

export const useLeadRouting = (id, enabled = true) =>
  useQuery({
    queryKey: leadRoutingKey(id),
    queryFn: () => callOf(() => getLeadRouting(id), "Lead routing did not load."),
    enabled: Boolean(id) && enabled,
  });

export const useSaveLeadRouting = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (routing) => callOf(() => saveLeadRouting(id, routing), "Lead routing did not save."),
    onSuccess: (data) => queryClient.setQueryData(leadRoutingKey(id), data),
  });
};

export const useStartBrokeragePlanCheckout = (id) =>
  useMutation({
    mutationFn: (input) => callOf(() => startBrokeragePlanCheckout(id, input), "Checkout did not open. Try again."),
  });

export const useOpenBrokeragePlanPortal = (id) =>
  useMutation({
    mutationFn: () => callOf(() => openBrokeragePlanPortal(id), "Billing did not open. Try again."),
  });

const refreshPlan = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: managedBrokerageKey(id) });
  queryClient.invalidateQueries({ queryKey: ["officeResults", id] });
  queryClient.invalidateQueries({ queryKey: leadRoutingKey(id) });
  queryClient.invalidateQueries({ queryKey: proRequestsKey(id) });
  queryClient.invalidateQueries({ queryKey: ["plans", "me"] });
};

export const useConfirmBrokeragePlanCheckout = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId) =>
      callOf(() => confirmBrokeragePlanCheckout(id, sessionId), "We could not confirm your payment yet."),
    onSuccess: () => refreshPlan(queryClient, id),
  });
};

export const seatErrorMessage = (fields) => {
  if (fields?.plan === "inactive") return "Start the brokerage plan before covering agents' Pro.";
  if (fields?.seatUserIds === "tooMany") return "That is more agents than your office has on the roster.";
  return null;
};

export const useSaveBrokerageSeats = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (seatUserIds) => {
      try {
        return await callOf(() => saveBrokerageSeats(id, seatUserIds), "Your Pro seats did not save.");
      } catch (error) {
        const message = seatErrorMessage(error.fields);
        if (message) error.message = message;
        throw error;
      }
    },
    onSuccess: () => refreshPlan(queryClient, id),
  });
};

export const proRequestsKey = (id) => ["proRequests", id];

export const useProRequests = (id, enabled = true) =>
  useQuery({
    queryKey: proRequestsKey(id),
    queryFn: () => callOf(() => getProRequests(id), "Pro requests did not load."),
    enabled: Boolean(id) && enabled,
  });

export const useDeclineProRequest = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId) => callOf(() => declineProRequest(id, requestId), "That request did not update."),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: proRequestsKey(id) }),
  });
};
